<script setup>
import { reactive, computed, onMounted, onBeforeUnmount, onUnmounted, watch, ref, nextTick } from 'vue'

import { useGlobalStore } from '@/stores/useGlobalStore'
import { useBoxStore } from '@/stores/useBoxStore'
import { useUserStore } from '@/stores/useUserStore'
import { useSpaceStore } from '@/stores/useSpaceStore'
import { useBroadcastStore } from '@/stores/useBroadcastStore'

import ColorPicker from '@/components/dialogs/ColorPicker.vue'
import BoxActions from '@/components/subsections/BoxActions.vue'
import ItemDetailsCheckboxButton from '@/components/ItemDetailsCheckboxButton.vue'
import BackgroundPicker from '@/components/dialogs/BackgroundPicker.vue'
import BackgroundPreview from '@/components/BackgroundPreview.vue'
import ItemDetailsDebug from '@/components/ItemDetailsDebug.vue'
import ItemTips from '@/components/dialogs/ItemTips.vue'
import { useOnscreenKeyboard } from '@/composables/useOnscreenKeyboard.js'
import utils from '@/utils.js'

import { colord } from 'colord'

const globalStore = useGlobalStore()
const boxStore = useBoxStore()
const userStore = useUserStore()
const spaceStore = useSpaceStore()
const broadcastStore = useBroadcastStore()

const dialogElement = ref(null)
const nameElement = ref(null)
const { addOnscreenKeyboardListener, removeOnscreenKeyboardListener } = useOnscreenKeyboard(nameElement)

let prevBoxId

const state = reactive({
  colorPickerIsVisible: false,
  isUpdated: false,
  backgroundPickerIsVisible: false,
  itemTipsIsVisible: false
})

const canEditBox = computed(() => userStore.getUserCanEditBox(currentBox.value))
const id = computed(() => globalStore.boxDetailsIsVisibleForBoxId)
// box state

const currentBox = computed(() => {
  return boxStore.getBox(id.value) || {}
})
watch(() => id.value, async (value, prevValue) => {
  await nextTick()
  // open
  if (value) {
    prevBoxId = value.id
    closeDialogs()
    broadcastShowBoxDetails()
    scrollIntoViewAndFocus()
    textareaSizes()
  // close
  } else {
    removeOnscreenKeyboardListener()
    if (!state.isUpdated) { return }
    state.isUpdated = false
  }
})

const visible = computed(() => utils.objectHasKeys(currentBox.value))
watch(() => visible.value, async (value, prevValue) => {
  await nextTick()
  if (!value) {
    globalStore.currentDraggingBoxId = ''
    globalStore.updateMultipleBoxesSelectedIds([])
    globalStore.preventMultipleSelectedActionsIsVisible = false
  }
})

const broadcastShowBoxDetails = () => {
  if (!canEditBox.value) { return }
  const updates = {
    boxId: currentBox.value.id,
    userId: userStore.id
  }
  broadcastStore.update({ updates, action: 'updateRemoteBoxDetailsVisible' })
}
const update = (updates) => {
  const keys = Object.keys(updates)
  const update = { id: currentBox.value.id }
  keys.forEach(key => {
    update[key] = updates[key]
  })
  boxStore.updateBox(update)
  boxStore.updateBoxInfoDimensions(update)
  state.isUpdated = true
}

// styles

const styles = computed(() => {
  const position = globalStore.getSpacePositionInPage(currentBox.value)
  const backgroundColor = colord(currentBox.value.color).alpha(1).toRgbString()
  return {
    left: `${position.x + 8}px`,
    top: `${position.y + 8}px`,
    backgroundColor
  }
})

// name

const name = computed({
  get () {
    return currentBox.value.name
  },
  set (name) {
    update({ name })
    textareaSizes()
  }
})
const isDefaultName = computed(() => {
  if (!name.value) { return true }
  // https://regexr.com/8okka
  const bool = /^Box \d+$/.test(name.value)
  return bool
})
const focusName = async () => {
  await nextTick()
  const element = nameElement.value
  const length = name.value.length
  utils.focusTextarea(element)
  selectName()
  globalStore.triggerUpdateHeaderAndFooterPosition()
}
const selectName = () => {
  // select all in new boxes, else put cursor at end (like cards)
  const currentBoxIsNew = globalStore.currentBoxIsNew
  const element = nameElement.value
  const length = name.value.length
  if (length && element) {
    element.setSelectionRange(0, length)
  }
  globalStore.currentBoxIsNew = false
}
const textareaSizes = () => {
  const element = dialogElement.value
  const textarea = element.querySelector('textarea')
  let modifier = 0
  if (canEditBox.value) {
    modifier = 1
  }
  textarea.style.height = textarea.scrollHeight + modifier + 'px'
}

// text edit actions

const selectionStartPosition = () => {
  if (!nameElement.value) { return }
  const startPosition = nameElement.value.selectionStart
  return startPosition
}
const selectionEndPosition = () => {
  if (!nameElement.value) { return }
  const endPosition = nameElement.value.selectionEnd
  return endPosition
}
const setSelectionRange = (start, end) => {
  if (nameElement.value) {
    nameElement.value.setSelectionRange(start, end)
  }
}
const toggleTextEditAction = async (action) => {
  if (!visible.value) { return }
  const startPosition = selectionStartPosition()
  const endPosition = selectionEndPosition()
  const { newName, offset } = utils.nameTextEditAction({
    action,
    startPosition,
    endPosition,
    name: name.value
  })
  update({ name: newName })
  textareaSizes()
  await nextTick()
  setSelectionRange(startPosition + offset, endPosition + offset)
}
const textEditLinkAction = async () => {
  if (!visible.value) { return }
  const { newName, newStartPosition, newEndPosition } = utils.nameTextEditLinkAction({
    startPosition: selectionStartPosition(),
    endPosition: selectionEndPosition(),
    name: name.value
  })
  update({ name: newName })
  textareaSizes()
  await nextTick()
  setSelectionRange(newStartPosition, newEndPosition)
}

// colors

const itemColors = computed(() => spaceStore.getSpaceItemColors.box)
const colorisDark = computed(() => {
  const color = currentBox.value.color
  return utils.colorIsDark(color)
})
const toggleColorPicker = () => {
  const value = !state.colorPickerIsVisible
  closeDialogs()
  state.colorPickerIsVisible = value
}
const updateColor = (color) => {
  update({ color })
}
const isThemeDarkAndUserColorLight = computed(() => {
  const isThemeDark = userStore.theme === 'dark'
  return isThemeDark && !colorisDark.value
})

// background

const toggleBackgroundPickerIsVisible = () => {
  const value = !state.backgroundPickerIsVisible
  closeDialogs()
  state.backgroundPickerIsVisible = value
}
const warningBackgroundFillIsEmptyIsVisible = computed(() => {
  return currentBox.value.background && currentBox.value.fill === 'empty'
})

// remove

const removeBox = () => {
  boxStore.removeBox(currentBox.value.id)
}

// dialog state

const closeDialogs = () => {
  state.colorPickerIsVisible = false
  state.backgroundPickerIsVisible = false
  state.itemTipsIsVisible = false
}
const closeAllDialogs = () => {
  globalStore.closeAllDialogs()
}
const handleEnterKey = (event) => {
  if (utils.isCompositionKeyboardEvent(event)) { return }
  event.preventDefault()
  closeAllDialogs()
}
const blur = () => {
  globalStore.triggerUpdateHeaderAndFooterPosition()
}
const scrollIntoView = async (behavior) => {
  await nextTick()
  const element = dialogElement.value
  await nextTick()
  await globalStore.scrollElementIntoView({ element, behavior })
}
const scrollIntoViewAndFocus = async () => {
  let behavior
  if (utils.isMobile()) {
    behavior = 'auto'
  }
  await nextTick()
  await scrollIntoView(behavior)
  addOnscreenKeyboardListener()
  focusName()
}

// filter

const isFilteredInSpace = computed({
  get () {
    const boxIds = globalStore.filteredBoxIds
    return boxIds.includes(currentBox.value.id)
  },
  set () {
    toggleFilteredInSpace()
  }
})
const toggleFilteredInSpace = () => {
  const filtered = globalStore.filteredBoxIds
  const boxId = currentBox.value.id
  if (filtered.includes(boxId)) {
    globalStore.removeFromFilteredBoxId(boxId)
  } else {
    globalStore.addToFilteredBoxId(boxId)
  }
}

// tips

const toggleItemTipsIsVisible = () => {
  const value = !state.itemTipsIsVisible
  closeDialogs()
  state.itemTipsIsVisible = value
}
</script>

<template lang="pug">
dialog.narrow.box-details(v-if="visible" :open="visible" @click.left.stop="closeDialogs" ref="dialogElement" :style="styles" :data-box-id="currentBox.id")
  .dark-theme-background-layer(v-if="isThemeDarkAndUserColorLight")
  section
    .row.info-row
      //- color
      .button-wrap
        button.change-color(:disabled="!canEditBox" @click.left.stop="toggleColorPicker" :class="{active: state.colorPickerIsVisible}")
          .current-color(:style="{backgroundColor: currentBox.color}")
        ColorPicker(
          :currentColor="currentBox.color"
          :visible="state.colorPickerIsVisible"
          :recentColors="itemColors"
          :luminosityIsDark="true"
          @selectedColor="updateColor"
        )
      //- name
      .textarea-wrap
        textarea.name.on-background-color(
          :disabled="!canEditBox"
          ref="nameElement"
          rows="1"
          placeholder="Box Name"
          v-model="name"
          @keydown.enter.stop="handleEnterKey"
          maxLength="600"
          :class="{'is-dark': colorisDark, 'is-light': !colorisDark}"
          @keydown.meta.b.exact.stop.prevent="toggleTextEditAction('bold')"
          @keydown.ctrl.b.exact.stop.prevent="toggleTextEditAction('bold')"
          @keydown.meta.i.exact.stop.prevent="toggleTextEditAction('italic')"
          @keydown.ctrl.i.exact.stop.prevent="toggleTextEditAction('italic')"
          @keydown.meta.k.exact.stop.prevent="textEditLinkAction"
          @keydown.ctrl.k.exact.stop.prevent="textEditLinkAction"
        )
      //- item tips
      .button-wrap.inline-button-wrap(v-if="isDefaultName" @click.left.stop="toggleItemTipsIsVisible" :class="{ active: state.itemTipsIsVisible }")
        button.small-button(tabindex="-1" :class="{ active: state.itemTipsIsVisible }")
          span ?
        ItemTips(:visible="state.itemTipsIsVisible")
      //- Filter
      .button-wrap.inline-button-wrap(title="Toggle focus filter")
        button.small-button(@click.left.prevent="toggleFilteredInSpace" @keydown.stop.enter="toggleFilteredInSpace" :class="{active: isFilteredInSpace}")
          img.icon(src="@/assets/filter.svg")

    .row(v-if="canEditBox")
      //- remove
      .button-wrap
        button.danger(@click.left="removeBox" title="Remove Box")
          img.icon(src="@/assets/remove.svg")
      //- [·]
      ItemDetailsCheckboxButton(:boxes="[currentBox]" :isDisabled="!canEditBox")
      .button-wrap.background-preview-wrap(@click.left.stop="toggleBackgroundPickerIsVisible")
        BackgroundPreview(:box="currentBox" :isButton="true" :buttonIsActive="state.backgroundPickerIsVisible")
        BackgroundPicker(:visible="state.backgroundPickerIsVisible" :box="currentBox")

    template(v-if="canEditBox")
      BoxActions(:visible="canEditBox" :boxes="[currentBox]" @closeDialogs="closeDialogs" :colorIsHidden="true")
    .row(v-if="!canEditBox")
      span.badge.info
        img.icon(src="@/assets/unlock.svg")
        span Read Only

    p.badge.info(v-if="warningBackgroundFillIsEmptyIsVisible")
      span Background image won't be drawn because fill mode is set to empty{{' '}}
      img.icon.box-icon(src="@/assets/box-empty.svg")

    ItemDetailsDebug(:item="currentBox" :keys="['x', 'y']")
</template>

<style lang="stylus">
dialog.box-details
  transform-origin top left
  .info-row
    align-items flex-start
  .inline-button-wrap
    margin 0
    padding 0
    padding-left 5px
    padding-top 1px
  .background-preview-wrap
    height var(--button-fixed-height)
</style>
